// 省份简称拼音数据
export interface ProvinceAbbreviation {
  name: string;           // 省份全名
  abbreviation: string;   // 简称汉字
  pinyin: string;         // 拼音
  alternativeAbbr?: string; // 备用简称（如四川的"蜀"）
  alternativePinyin?: string; // 备用拼音
}

export const PROVINCE_ABBREVIATIONS: ProvinceAbbreviation[] = [
  { name: '北京市', abbreviation: '京', pinyin: 'jīng' },
  { name: '天津市', abbreviation: '津', pinyin: 'jīn' },
  { name: '河北省', abbreviation: '冀', pinyin: 'jì' },
  { name: '山西省', abbreviation: '晋', pinyin: 'jìn' },
  { name: '内蒙古自治区', abbreviation: '蒙', pinyin: 'méng' },
  { name: '辽宁省', abbreviation: '辽', pinyin: 'liáo' },
  { name: '吉林省', abbreviation: '吉', pinyin: 'jí' },
  { name: '黑龙江省', abbreviation: '黑', pinyin: 'hēi' },
  { name: '上海市', abbreviation: '沪', pinyin: 'hù' },
  { name: '江苏省', abbreviation: '苏', pinyin: 'sū' },
  { name: '浙江省', abbreviation: '浙', pinyin: 'zhè' },
  { name: '安徽省', abbreviation: '皖', pinyin: 'wǎn' },
  { name: '福建省', abbreviation: '闽', pinyin: 'mǐn' },
  { name: '江西省', abbreviation: '赣', pinyin: 'gàn' },
  { name: '山东省', abbreviation: '鲁', pinyin: 'lǔ' },
  { name: '河南省', abbreviation: '豫', pinyin: 'yù' },
  { name: '湖北省', abbreviation: '鄂', pinyin: 'è' },
  { name: '湖南省', abbreviation: '湘', pinyin: 'xiāng' },
  { name: '广东省', abbreviation: '粤', pinyin: 'yuè' },
  { name: '广西壮族自治区', abbreviation: '桂', pinyin: 'guì' },
  { name: '海南省', abbreviation: '琼', pinyin: 'qióng' },
  { name: '重庆市', abbreviation: '渝', pinyin: 'yú' },
  { 
    name: '四川省', 
    abbreviation: '川', 
    pinyin: 'chuān',
    alternativeAbbr: '蜀',
    alternativePinyin: 'shǔ'
  },
  { 
    name: '贵州省', 
    abbreviation: '黔', 
    pinyin: 'qián',
    alternativeAbbr: '贵',
    alternativePinyin: 'guì'
  },
  { 
    name: '云南省', 
    abbreviation: '滇', 
    pinyin: 'diān',
    alternativeAbbr: '云',
    alternativePinyin: 'yún'
  },
  { name: '西藏自治区', abbreviation: '藏', pinyin: 'zàng' },
  { 
    name: '陕西省', 
    abbreviation: '陕', 
    pinyin: 'shǎn',
    alternativeAbbr: '秦',
    alternativePinyin: 'qín'
  },
  { 
    name: '甘肃省', 
    abbreviation: '甘', 
    pinyin: 'gān',
    alternativeAbbr: '陇',
    alternativePinyin: 'lǒng'
  },
  { name: '青海省', abbreviation: '青', pinyin: 'qīng' },
  { name: '宁夏回族自治区', abbreviation: '宁', pinyin: 'níng' },
  { name: '新疆维吾尔自治区', abbreviation: '新', pinyin: 'xīn' },
  { name: '香港特别行政区', abbreviation: '港', pinyin: 'gǎng' },
  { name: '澳门特别行政区', abbreviation: '澳', pinyin: 'ào' },
  { name: '台湾省', abbreviation: '台', pinyin: 'tái' }
];

// 根据省份名称获取简称信息
export const getProvinceAbbreviation = (provinceName: string): ProvinceAbbreviation | null => {
  return PROVINCE_ABBREVIATIONS.find(item => item.name === provinceName) || null;
};

// 获取简称显示文本（包含拼音）
export const getAbbreviationDisplay = (provinceName: string, showAlternative: boolean = false): string => {
  const abbr = getProvinceAbbreviation(provinceName);
  if (!abbr) return '';
  
  if (showAlternative && abbr.alternativeAbbr && abbr.alternativePinyin) {
    return `${abbr.alternativeAbbr} ${abbr.alternativePinyin}`;
  }
  
  return `${abbr.abbreviation} ${abbr.pinyin}`;
};

// 获取所有简称（用于显示选项）
export const getAllAbbreviationOptions = (provinceName: string): string[] => {
  const abbr = getProvinceAbbreviation(provinceName);
  if (!abbr) return [];
  
  const options = [`${abbr.abbreviation} ${abbr.pinyin}`];
  
  if (abbr.alternativeAbbr && abbr.alternativePinyin) {
    options.push(`${abbr.alternativeAbbr} ${abbr.alternativePinyin}`);
  }
  
  return options;
};

// 检查是否有备用简称
export const hasAlternativeAbbreviation = (provinceName: string): boolean => {
  const abbr = getProvinceAbbreviation(provinceName);
  return !!(abbr?.alternativeAbbr && abbr?.alternativePinyin);
};

// 字体大小配置选项
export type FontSizeOption = 'small' | 'medium' | 'large' | 'extra-large';

export interface FontSizeConfig {
  containerClass: string;
  abbreviationClass: string;
  pinyinClass: string;
  shadowClass: string;
}

export const FONT_SIZE_CONFIGS: Record<FontSizeOption, FontSizeConfig> = {
  small: {
    containerClass: 'text-xs',
    abbreviationClass: 'text-sm font-black',
    pinyinClass: 'text-xs font-medium opacity-90',
    shadowClass: 'text-shadow-sm'
  },
  medium: {
    containerClass: 'text-sm',
    abbreviationClass: 'text-base font-black',
    pinyinClass: 'text-sm font-bold opacity-95',
    shadowClass: 'text-shadow-md'
  },
  large: {
    containerClass: 'text-sm',
    abbreviationClass: 'text-lg font-black',
    pinyinClass: 'text-sm font-bold opacity-95',
    shadowClass: 'text-shadow-md'
  },
  'extra-large': {
    containerClass: 'text-base',
    abbreviationClass: 'text-xl font-black',
    pinyinClass: 'text-base font-bold opacity-95',
    shadowClass: 'text-shadow-lg'
  }
};

// 获取字体大小配置
export const getFontSizeConfig = (size: FontSizeOption = 'large'): FontSizeConfig => {
  return FONT_SIZE_CONFIGS[size];
};

// 格式化显示文本（用于不同的显示场景）
export const formatAbbreviationDisplay = (
  provinceName: string, 
  options: {
    showPinyin?: boolean;
    showAlternative?: boolean;
    separator?: string;
  } = {}
): string => {
  const {
    showPinyin = true,
    showAlternative = false,
    separator = ' '
  } = options;
  
  const abbr = getProvinceAbbreviation(provinceName);
  if (!abbr) return '';
  
  let targetAbbr = abbr.abbreviation;
  let targetPinyin = abbr.pinyin;
  
  if (showAlternative && abbr.alternativeAbbr && abbr.alternativePinyin) {
    targetAbbr = abbr.alternativeAbbr;
    targetPinyin = abbr.alternativePinyin;
  }
  
  if (showPinyin) {
    return `${targetAbbr}${separator}${targetPinyin}`;
  }
  
  return targetAbbr;
};
